import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../AppIcon';
import Image from '../../../AppImage';
import Button from '../../../ui/Button';

const HeroSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [selectedRole, setSelectedRole] = useState(null);

  const heroSlides = [
  {
    title: "Bridge Innovation Gaps with Intelligent Matching",
    subtitle: "Connect corporations with breakthrough startups through verified intermediary networks",
    image: "https://images.unsplash.com/photo-1717169196118-25d850bd5f9e",
    imageAlt: "Modern office building with glass facade reflecting blue sky and clouds"
  },
  {
    title: "Real-Time Scouting Meets Data-Driven Results",
    subtitle: "Field-verified startup intelligence with performance tracking and ROI measurement",
    image: "https://images.unsplash.com/photo-1735469157670-1212e570eadc",
    imageAlt: "Business professionals analyzing data charts on multiple computer screens in modern office"
  },
  {
    title: "Transform Networks into Revenue Streams",
    subtitle: "Monetize your startup ecosystem connections through performance-based referrals",
    image: "https://images.unsplash.com/photo-1660063093662-1f76f500f0ff",
    imageAlt: "Financial growth chart with upward trending arrow and business documents on desk"
  }];


  const roleCards = [
  {
    id: 'corporate',
    title: 'Corporate Innovation Teams',
    description: 'Discover verified startups through intelligent matching and reduce partnership risks',
    icon: 'Building2',
    color: 'from-blue-500 to-blue-600',
    stats: '2,500+ Programs',
    route: '/multi-role-dashboard'
  },
  {
    id: 'startup',
    title: 'Growth-Stage Startups',
    description: 'Access corporate partnerships and funding through verified intermediary networks',
    icon: 'Rocket',
    color: 'from-emerald-500 to-emerald-600',
    stats: '15,000+ Startups',
    route: '/program-discovery-engine'
  },
  {
    id: 'intermediary',
    title: 'Professional Intermediaries',
    description: 'Monetize your network with performance-based referral tracking and analytics',
    icon: 'Users',
    color: 'from-purple-500 to-purple-600',
    stats: '8,500+ Intermediaries',
    route: '/referral-performance-center'
  },
  {
    id: 'field',
    title: 'Field Operations Staff',
    description: 'Collect and verify startup data with mobile-optimized tools and offline capabilities',
    icon: 'MapPin',
    color: 'from-orange-500 to-orange-600',
    stats: '3,200+ Field Scouts',
    route: '/real-time-scouting-interface'
  }];


  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides?.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section className="relative min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}></div>
      </div>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
        {/* Hero Content */}
        <div className="grid lg:grid-cols-2 gap-12 items-center min-h-[80vh]">
          {/* Left Content */}
          <div className="space-y-8 animate-fade-in">
            <div className="space-y-4">
              <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium">
                <Icon name="Zap" size={16} />
                <span>Intelligent Innovation Ecosystem</span>
              </div>
              
              <h1 className="text-4xl lg:text-6xl font-bold text-slate-900 leading-tight">
                {heroSlides?.[currentSlide]?.title}
              </h1>
              
              <p className="text-xl text-slate-600 leading-relaxed">
                {heroSlides?.[currentSlide]?.subtitle}
              </p>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                variant="default"
                size="lg"
                iconName="ArrowRight"
                iconPosition="right"
                className="bg-gradient-to-r from-primary to-accent hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200">

                Start Matching Now
              </Button>
              
              <Button
                variant="outline"
                size="lg"
                iconName="Play"
                iconPosition="left"
                className="border-slate-300 hover:bg-slate-50">

                Watch Demo
              </Button>
            </div>

            {/* Trust Indicators */}
            <div className="flex items-center space-x-6 pt-4">
              <div className="flex items-center space-x-2">
                <Icon name="Shield" size={20} className="text-emerald-500" />
                <span className="text-sm text-slate-600">SOC 2 Certified</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Award" size={20} className="text-blue-500" />
                <span className="text-sm text-slate-600">ISO 27001</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Users" size={20} className="text-purple-500" />
                <span className="text-sm text-slate-600">29,200+ Active Users</span>
              </div>
            </div>
          </div>

          {/* Right Content - Hero Image */}
          <div className="relative">
            <div className="relative h-96 lg:h-[500px] rounded-2xl overflow-hidden shadow-2xl">
              <Image
                src={heroSlides?.[currentSlide]?.image}
                alt={heroSlides?.[currentSlide]?.imageAlt}
                className="w-full h-full object-cover transition-all duration-1000" />

              
              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
              
              {/* Floating Stats */}
              <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm rounded-lg p-3 shadow-lg">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                  <span className="text-sm font-medium text-slate-700">Live Matching</span>
                </div>
              </div>
              
              <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm rounded-lg p-3 shadow-lg">
                <div className="text-sm text-slate-600">Success Rate</div>
                <div className="text-2xl font-bold text-slate-900">94.2%</div>
              </div>
            </div>

            {/* Slide Indicators */}
            <div className="flex justify-center space-x-2 mt-6">
              {heroSlides?.map((_, index) =>
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-3 h-3 rounded-full transition-all duration-200 ${
                index === currentSlide ? 'bg-primary' : 'bg-slate-300'}`
                } />

              )}
            </div>
          </div>
        </div>

        {/* Role Selection Cards */}
        <div className="mt-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Choose Your Innovation Journey
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              ScoutBridge adapts to your role in the innovation ecosystem. Select your path to get started.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {roleCards?.map((role) =>
            <div
              key={role?.id}
              className={`group relative bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer border-2 ${
              selectedRole === role?.id ? 'border-primary' : 'border-transparent hover:border-slate-200'}`
              }
              onClick={() => setSelectedRole(role?.id)}>

                {/* Icon */}
                <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${role?.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-200`}>
                  <Icon name={role?.icon} size={24} color="white" />
                </div>

                {/* Content */}
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  {role?.title}
                </h3>
                <p className="text-sm text-slate-600 mb-4 leading-relaxed">
                  {role?.description}
                </p>

                {/* Stats */}
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-slate-500">
                    {role?.stats}
                  </span>
                  <Link
                  to={role?.route}
                  className="inline-flex items-center text-primary hover:text-primary/80 transition-colors duration-200">

                    <span className="text-sm font-medium">Get Started</span>
                    <Icon name="ArrowRight" size={16} className="ml-1" />
                  </Link>
                </div>

                {/* Selection Indicator */}
                {selectedRole === role?.id &&
              <div className="absolute top-4 right-4">
                    <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                      <Icon name="Check" size={14} color="white" />
                    </div>
                  </div>
              }
              </div>
            )}
          </div>
        </div>
      </div>
    </section>);

};

export default HeroSection;